import axios from 'axios';

// ═══════════════════════════════════════════════════════
// API Configuration - Production
// ═══════════════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://turabgoldshop.cloud/';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    timeout: 15000,
});

// ═══════════════════════════════════════════════════════
// Request Interceptor
// ═══════════════════════════════════════════════════════
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Token ${token}`;
    }

    // Add timestamp to avoid caching issues for GET requests
    if (config.method === 'get') {
        config.params = {
            ...config.params,
            _t: Date.now()
        };
    }

    return config;
}, (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
});

// ═══════════════════════════════════════════════════════
// Response Interceptor
// ═══════════════════════════════════════════════════════
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        const { response } = error;

        // Handle 401 - Unauthorized
        if (response?.status === 401) {
            const isAuthRequest = response.config?.url?.includes('/auth/');

            if (!isAuthRequest) {
                console.warn('Authentication expired, clearing token');
                localStorage.removeItem('token');
                localStorage.removeItem('user_role');
                localStorage.removeItem('user_data');

                // Only redirect if we're not already on login page
                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login?session=expired';
                }
            }
        }

        // Handle 403 - Forbidden
        if (response?.status === 403) {
            console.warn('Insufficient permissions for this action');
        }

        // Handle 404 - Not Found
        if (response?.status === 404) {
            console.warn('Endpoint not found:', response.config.url);
        }

        // Enhanced error logging
        if (response?.status >= 500) {
            console.error('Server error:', response.status, response.data);
        }

        return Promise.reject(error);
    }
);

// ═══════════════════════════════════════════════════════
// Authentication API
// ═══════════════════════════════════════════════════════
const authApi = {
    login: (username, password) => api.post('api/auth/login/', { username, password }),
    register: (userData) => api.post('api/auth/register/', userData),
    logout: () => api.post('api/auth/logout/'),
    getCurrentUser: () => api.get('api/users/me/'),
    changePassword: (data) => api.post('api/users/change_password/', data)
};

// ═══════════════════════════════════════════════════════
// Gold API
// ═══════════════════════════════════════════════════════
const goldApi = {
    // =============== GOLD RATES ===============
    getTodayGoldRate: () => api.get('api/gold-rates/today/'),
    setTodayGoldRate: (data) => api.post('api/gold-rates/set_today/', data),
    getGoldRateHistory: (params) => api.get('api/gold-rate-history/', { params }),
    getGoldRateTrend: () => api.get('api/gold-rates/trend/'),
    calculateGoldValue: (data) => api.post('api/gold-rates/calculate/', data),

    // =============== CUSTOMERS ===============
    getCustomers: (params = {}) => {
        console.log('Fetching customers with params:', params);
        return api.get('api/customers/', { params });
    },
    createCustomer: (data) => api.post('api/customers/', data),
    updateCustomer: (id, data) => api.put(`api/customers/${id}/`, data),
    deleteCustomer: (id) => api.delete(`api/customers/${id}/`),
    getCustomerTransactions: (customerId) => api.get(`api/customers/${customerId}/transactions/`),
    getCustomerDebts: (customerId) => api.get(`api/customers/${customerId}/debts/`),
    updateCustomerCreditLimit: (customerId, creditLimit) => api.post(`api/customers/${customerId}/update_credit_limit/`, { credit_limit: creditLimit }),
    getCustomerSummary: () => api.get('api/customer-summary/'),

    // =============== BUY/SELL TRANSACTIONS ===============
    getBuySellTransactions: (params = {}) => {
        console.log('Fetching buy/sell transactions with params:', params);
        return api.get('api/buy-sell-transactions/', { params });
    },
    createBuySellTransaction: (data) => {
        console.log('Creating buy/sell transaction:', data);
        return api.post('api/buy-sell-transactions/', data);
    },
    getBuySellTransaction: (id) => api.get(`api/buy-sell-transactions/${id}/`),
    updateBuySellTransaction: (id, data) => api.put(`api/buy-sell-transactions/${id}/`, data),
    deleteBuySellTransaction: (id) => api.delete(`api/buy-sell-transactions/${id}/`),

    addTransactionPayment: (transactionId, data) => api.post(`api/buy-sell-transactions/${transactionId}/add_payment/`, data),
    cancelTransaction: (transactionId) => api.post(`api/buy-sell-transactions/${transactionId}/cancel/`),

    getTransactionSummary: () => api.get('api/buy-sell-summary/'),
    exportTransactions: (params = {}) => {
        return api.post('api/export-transactions/', params, {
            responseType: 'blob',
            headers: {
                'Accept': 'text/csv, application/json'
            }
        });
    },

    // =============== INVENTORY ===============
    getGoldItems: (params = {}) => {
        console.log('Fetching gold items with params:', params);
        return api.get('api/gold-items/', { params });
    },
    createGoldItem: (data) => api.post('api/gold-items/', data),
    updateGoldItem: (id, data) => api.put(`api/gold-items/${id}/`, data),
    deleteGoldItem: (id) => api.delete(`api/gold-items/${id}/`),
    getLowStockItems: () => api.get('api/gold-items/low_stock/'),
    getInventorySummary: () => api.get('api/gold-items/summary/'),

    // =============== GOLD ITEM CATEGORIES ===============
    getItemCategories: (params = {}) => api.get('api/item-categories/', { params }),
    createItemCategory: (data) => api.post('api/item-categories/', data),

    // =============== DEBTS & PAYMENTS ===============
    getDebts: (params = {}) => api.get('api/debts/', { params }),
    createDebt: (data) => api.post('api/debts/', data),
    getDebtPayments: (params = {}) => api.get('api/debt-payments/', { params }),
    createDebtPayment: (data) => api.post('api/debt-payments/', data),
    getDebtSummary: () => api.get('api/debt-summary/'),

    // =============== INVOICES ===============
    getInvoices: (params = {}) => api.get('api/invoices/', { params }),
    createInvoice: (data) => api.post('api/invoices/', data),
    generateInvoice: (transactionId) => api.post(`api/invoices/generate/${transactionId}/`),

    // =============== OLD TRANSACTIONS ===============
    getTransactions: (params = {}) => api.get('api/transactions/', { params }),
    createTransaction: (data) => api.post('api/transactions/', data),
    getTodayTransactions: () => api.get('api/transactions/today/'),

    // =============== PAYMENTS ===============
    createPayment: (data) => api.post('api/payments/', data),
    getTransactionPayments: (transactionId) => api.get(`api/payments/by_transaction/?transaction_id=${transactionId}`),

    // =============== DASHBOARD ===============
    getDashboardStats: () => api.get('api/dashboard/'),

    // =============== EXPENSES ===============
    getExpenses: (params = {}) => api.get('api/expenses/', { params }),
    createExpense: (data) => api.post('api/expenses/', data),
    getExpenseSummary: () => api.get('api/expenses/summary/'),

    // =============== EMPLOYEES ===============
    getEmployees: (params = {}) => api.get('api/employees/', { params }),
    createEmployee: (data) => api.post('api/employees/', data),

    // =============== SECURITY ===============
    getSecurityLogs: (params = {}) => api.get('api/security-logs/', { params }),

    // =============== SYSTEM SETTINGS ===============
    getSystemSettings: () => api.get('api/system-settings/'),
    updateSystemSetting: (key, value) => api.post('api/system-settings/', { key, value }),

    // =============== BACKUP & EXPORT ===============
    createBackup: (data) => api.post('api/backup/', data),
    exportData: (type) => api.get(`api/export/?type=${type}`),

    // =============== UTILITY FUNCTIONS ===============
    calculateGoldValue: (weight, karat, goldRate, weightUnit = 'gram') => {
        let weightInGrams = weight;
        if (weightUnit === 'tola') {
            weightInGrams = weight * 11.6638;
        } else if (weightUnit === 'mithqal') {
            weightInGrams = weight * 4.3748;
        }

        let ratePerGram = 0;
        if (goldRate) {
            switch(karat) {
                case 24: ratePerGram = goldRate.gold_24k_per_gram; break;
                case 22: ratePerGram = goldRate.gold_22k_per_gram; break;
                case 21: ratePerGram = goldRate.gold_21k_per_gram || (goldRate.gold_22k_per_gram * 0.95); break;
                case 18: ratePerGram = goldRate.gold_18k_per_gram; break;
                case 14: ratePerGram = goldRate.gold_14k_per_gram; break;
                default: ratePerGram = goldRate.gold_24k_per_gram * (karat / 24);
            }
        }

        return weightInGrams * ratePerGram;
    },

    formatCurrency: (amount) => {
        if (amount === undefined || amount === null) return 'AFN 0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'AFN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    },

    formatWeight: (weight, unit) => {
        const unitSymbols = {
            'gram': 'g',
            'tola': 'tola',
            'mithqal': 'mithqal'
        };
        return `${parseFloat(weight).toFixed(3)} ${unitSymbols[unit] || unit}`;
    },

    generateTransactionNumber: (type = 'sell') => {
        const prefix = type === 'sell' ? 'SALE' : type === 'buy' ? 'BUY' : 'TRX';
        const timestamp = Date.now().toString().slice(-8);
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `${prefix}-${timestamp}-${random}`;
    }
};

// ═══════════════════════════════════════════════════════
// AI API
// ═══════════════════════════════════════════════════════
const aiApi = {
    predictGoldPrice: (days = 7) => api.get(`api/ai/predict-gold-price/?days=${days}`),
    getInventoryRecommendations: () => api.get('api/ai/inventory-recommendations/'),
    analyzeTransactionPatterns: (period = 'monthly') => api.get(`api/ai/transaction-patterns/?period=${period}`),
    getSalesForecast: (months = 3) => api.get(`api/ai/sales-forecast/?months=${months}`),
    analyzeCustomerSegmentation: () => api.get('api/ai/customer-segmentation/'),
    getAIAssistantResponse: (message, context = null) => api.post('api/ai/chat-assistant/', { message, context }),
    getFraudDetectionAlerts: (days = 7) => api.get(`api/ai/fraud-detection/?days=${days}`),
    getSeasonalTrends: () => api.get('api/ai/seasonal-trends/'),
    calculateReorderPoints: () => api.get('api/ai/reorder-points/'),
    getMarketTrends: () => api.get('api/ai/market-trends/'),
    getDashboardInsights: () => api.get('api/ai/dashboard-insights/'),
    testConnection: () => api.get('api/ai/test-connection/'),
    debugTest: () => api.get('api/ai/debug-test/'),
};

// ═══════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════
const clearAuthHeaders = () => {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_data');
};

const setAuthToken = (token, userData = null) => {
    if (token) {
        api.defaults.headers.common['Authorization'] = `Token ${token}`;
        if (userData) {
            localStorage.setItem('user_role', userData.role);
            localStorage.setItem('user_data', JSON.stringify(userData));
        }
    } else {
        clearAuthHeaders();
    }
};

const getCurrentUserRole = () => {
    return localStorage.getItem('user_role');
};

const getCurrentUser = () => {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
};

const hasRole = (role) => {
    const userRole = getCurrentUserRole();
    return userRole === role;
};

const hasAnyRole = (roles) => {
    const userRole = getCurrentUserRole();
    return roles.includes(userRole);
};

// ═══════════════════════════════════════════════════════
// ROLE PERMISSIONS
// ═══════════════════════════════════════════════════════
const rolePermissions = {
    owner: ['all'],
    manager: ['dashboard', 'inventory', 'transactions', 'customers', 'employees', 'reports', 'financials', 'ai', 'buy_sell', 'debts'],
    sales: ['dashboard', 'inventory', 'transactions', 'customers', 'buy_sell'],
    accountant: ['dashboard', 'financials', 'reports', 'expenses', 'gold_rates', 'ai', 'buy_sell', 'debts'],
    security: ['dashboard', 'security_logs']
};

const canAccess = (resource) => {
    const userRole = getCurrentUserRole();
    if (!userRole) return false;
    const permissions = rolePermissions[userRole] || [];
    return permissions.includes('all') || permissions.includes(resource);
};

const canAccessBuySell = () => {
    const userRole = getCurrentUserRole();
    return ['owner', 'manager', 'sales', 'accountant'].includes(userRole);
};

const canCreateTransaction = () => {
    const userRole = getCurrentUserRole();
    return ['owner', 'manager', 'sales'].includes(userRole);
};

const canViewFinancialReports = () => {
    const userRole = getCurrentUserRole();
    return ['owner', 'manager', 'accountant'].includes(userRole);
};

const canUseAI = () => {
    const userRole = getCurrentUserRole();
    return ['owner', 'manager', 'accountant'].includes(userRole);
};

const canExportData = () => {
    const userRole = getCurrentUserRole();
    return ['owner', 'manager', 'accountant'].includes(userRole);
};

const canManageSettings = () => {
    const userRole = getCurrentUserRole();
    return ['owner', 'manager'].includes(userRole);
};

// ═══════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════
export {
    api,
    authApi,
    goldApi,
    aiApi,
    clearAuthHeaders,
    setAuthToken,
    getCurrentUserRole,
    getCurrentUser,
    hasRole,
    hasAnyRole,
    canAccess,
    canUseAI,
    canAccessBuySell,
    canCreateTransaction,
    canViewFinancialReports,
    canExportData,
    canManageSettings,
    rolePermissions
};